import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { validation } from "./helpers/validation";
import {
  TextField, Button, Typography, Box, Link,
  Grid, Container, InputAdornment, IconButton, CircularProgress
} from '@mui/material';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import { useState, useContext } from "react";
import { ClienteFormContext } from "../../../context/cliente/ClienteContext";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 'var(--radius-md)',
    fontFamily: 'var(--font-body)',
    backgroundColor: 'var(--bg-subtle)',
    transition: 'all 0.2s ease',
    '& fieldset': { border: '2px solid transparent' },
    '&:hover fieldset': { border: '2px solid var(--border-hover)' },
    '&.Mui-focused fieldset': { border: '2px solid var(--accent)' },
    '&.Mui-focused': { backgroundColor: 'var(--bg-card)' },
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'var(--font-body)',
    fontSize: '0.9rem',
  },
  '& .MuiFormHelperText-root': {
    fontFamily: 'var(--font-body)',
    fontSize: '0.72rem',
  },
};

export const Form = () => {
  const { postClienteData } = useContext(ClienteFormContext);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordRepeat, setShowPasswordRepeat] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(validation)
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    const formData = {
      cli_name: data.cli_name + " " + data.cli_apellido,
      cli_mail: data.cli_email,
      cli_pass: data.cli_pass,
    };
    try {
      await postClienteData(formData);
    } catch (error) {
      console.error("Error al registrar:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box
        sx={{
          minHeight: '85vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          py: 4,
          background: 'linear-gradient(135deg, var(--bg-main) 0%, var(--bg-subtle) 100%)',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 480,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: 'var(--bg-card)',
            p: { xs: 3, sm: 5 },
            borderRadius: 'var(--radius-xl)',
            border: '1px solid var(--border)',
            boxShadow: 'var(--shadow-lg)',
            animation: 'fadeInUp 0.5s ease both',
          }}
        >
          {/* Icon */}
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 'var(--radius-lg)',
              background: 'linear-gradient(135deg, var(--accent) 0%, #c0392b 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
              boxShadow: '0 4px 16px rgba(233,69,96,0.3)',
            }}
          >
            <PersonAddOutlinedIcon sx={{ color: '#fff', fontSize: 28 }} />
          </Box>

          <Typography
            component="h1"
            sx={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 800,
              fontSize: '1.5rem',
              color: 'var(--text-primary)',
              letterSpacing: '-0.02em',
              mb: 0.5,
            }}
          >
            Crear Cuenta
          </Typography>

          <Typography
            sx={{
              fontFamily: 'var(--font-body)',
              fontSize: '0.85rem',
              color: 'var(--text-muted)',
              mb: 3,
            }}
          >
            Registrate para empezar a comprar
          </Typography>

          {/* Fields */}
          <Grid container spacing={2} sx={{ width: '100%' }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombre"
                placeholder="Juan"
                {...register("cli_name")}
                error={!!errors.cli_name}
                helperText={errors.cli_name?.message}
                disabled={isSubmitting}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Apellido"
                placeholder="Pérez"
                {...register("cli_apellido")}
                error={!!errors.cli_apellido}
                helperText={errors.cli_apellido?.message}
                disabled={isSubmitting}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Correo electrónico"
                placeholder="tu@correo.com"
                {...register("cli_email")}
                error={!!errors.cli_email}
                helperText={errors.cli_email?.message}
                disabled={isSubmitting}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Contraseña"
                placeholder="••••••••"
                type={showPassword ? "text" : "password"}
                {...register("cli_pass")}
                error={!!errors.cli_pass}
                helperText={errors.cli_pass?.message}
                disabled={isSubmitting}
                sx={inputSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        tabIndex={-1}
                        disabled={isSubmitting}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Repetir contraseña"
                placeholder="••••••••"
                type={showPasswordRepeat ? "text" : "password"}
                {...register("cli_pass_confirm")}
                error={!!errors.cli_pass_confirm}
                helperText={errors.cli_pass_confirm?.message}
                disabled={isSubmitting}
                sx={inputSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPasswordRepeat(!showPasswordRepeat)}
                        tabIndex={-1}
                        disabled={isSubmitting}
                        edge="end"
                        size="small"
                      >
                        {showPasswordRepeat ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
          </Grid>

          {/* Submit */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={!isValid || isSubmitting}
            sx={{
              mt: 3,
              mb: 2,
              py: 1.5,
              borderRadius: 'var(--radius-md)',
              fontFamily: 'var(--font-heading)',
              fontWeight: 700,
              fontSize: '0.95rem',
              textTransform: 'none',
              backgroundColor: 'var(--accent)',
              boxShadow: '0 4px 12px rgba(233,69,96,0.3)',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'var(--accent-hover)',
                boxShadow: '0 6px 20px rgba(233,69,96,0.4)',
                transform: 'translateY(-1px)',
              },
              '&:active': { transform: 'scale(0.98)' },
              '&:disabled': {
                backgroundColor: 'var(--border)',
                boxShadow: 'none',
                color: 'var(--text-muted)',
              },
            }}
          >
            {isSubmitting ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : "Registrarme"}
          </Button>

          {/* Link login */}
          <Typography
            sx={{
              fontSize: '0.85rem',
              fontFamily: 'var(--font-body)',
              color: 'var(--text-secondary)',
              mt: 1,
            }}
          >
            ¿Ya tenés cuenta?{' '}
            <Link
              href="login"
              sx={{
                fontWeight: 700,
                color: 'var(--accent)',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Iniciá sesión
            </Link>
          </Typography>
        </Box>
      </Box>
    </form>
  );
};